import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	Col,
	Empty,
	Flex,
	Row,
	Space,
	Tabs,
	Tag,
	Typography,
} from 'antd';
import isEqual from 'lodash.isequal';
import {
	CloseOutlined,
	EditOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/pro-light-svg-icons';
import { splitActionKey } from '@shared/utils/splitActionKey';
import { capitalize } from '@shared/utils/string';
import {
	EstimateStatus,
	LaytimeTerms,
	PortActionTypes,
} from '@shared/utils/constants';
import {
	isDischargePort,
	isLoadPort,
} from '@shared/utils/cargoPortHelpers';
import type { CargoPortWithEstimatedValues } from '@api/features/cargos/transformCargos';
import type { GetCargoDetailsResponse } from '@api/features/cargos/getCargoDetails';
import type { Port } from '@api/utils/ports';
import Card from '@client/components/Card/Card';
import CountryFlag from '@client/components/CountryFlag';
import Button from '@client/components/Button';
import Select from '@client/components/Select';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import { getPorts } from '@client/lib/api';
import CargoPortForm from '../list/CargoPortForm';
import EstimatedCargoPortForm from '../list/EstimatedCargoPortForm';
import styles from './CargoDetailsScreen.module.css';
import { ModalModes } from './CargoDetailsScreen';

type CargoPortsDetailsProps = {
	mode: ModalModes;
	cargoPorts: CargoPortWithEstimatedValues[];
	sortedCargoPorts: CargoPortWithEstimatedValues[];
	cargoDetails: GetCargoDetailsResponse | null | undefined;
	handleChange: (values: { cargoPortId: number; cargoId: number; attributes: any }) => void;
	laytimeTerms: LaytimeTerms;
	quantitiesRemaining: {
		loadedRemaining: number;
		dischargeRemaining: number;
	} | null;
	refreshDetails: () => void;
	copyCargoPortSettings: (cargoPortId: number) => void;
	selectedCargoPortId?: number | null;
};

export const CargoPortsDetails = ({
	mode,
	cargoPorts,
	sortedCargoPorts,
	handleChange,
	cargoDetails,
	laytimeTerms,
	quantitiesRemaining,
	refreshDetails,
	copyCargoPortSettings,
	selectedCargoPortId,
}: CargoPortsDetailsProps) => {
	const creatingCargo = mode === 'creatingOnContract';
	const [editingPort, setEditingPort] = useState(false);
	const [portToEdit, setPortToEdit] = useState<number>();
	const [selectedCargoPort, setSelectedCargoPort] = useState<
		GetCargoDetailsResponse['cargoPorts'][number] | null
	>(null);
	const [activeTabKey, setActiveTabKey] = useState<string | null>(null);
	const appliedExternalCargoPortIdRef = useRef<number | null>(null);

	useEffect(() => {
		if (selectedCargoPortId == null) {
			appliedExternalCargoPortIdRef.current = null;

			return;
		}

		if (appliedExternalCargoPortIdRef.current === selectedCargoPortId) {
			return;
		}

		const selectedPort = cargoPorts.find((cp) => cp.id === selectedCargoPortId);

		if (selectedPort == null) {
			return;
		}

		appliedExternalCargoPortIdRef.current = selectedCargoPortId;
		setSelectedCargoPort(selectedPort);
		setActiveTabKey(String(selectedCargoPortId));
	}, [selectedCargoPortId, cargoPorts]);

	useEffect(() => {
		if (sortedCargoPorts.length === 0) {
			return;
		}

		setActiveTabKey((currentKey) => {
			if (currentKey != null && sortedCargoPorts.some((cp) => String(cp.id) === currentKey)) {
				return currentKey;
			}

			return String(sortedCargoPorts[0].id);
		});

		setSelectedCargoPort((current) => {
			if (current != null && sortedCargoPorts.some((cp) => cp.id === current.id)) {
				return current;
			}

			return sortedCargoPorts[0];
		});
	}, [sortedCargoPorts]);

	const disabled = cargoDetails?.status === EstimateStatus.Fixed && cargoDetails.voyageId == null;

	const [ports] = useFetchedState(getPorts);

	const portOptions = useMemo(() => (ports != null ? ports?.map((p) => {
		return {
			label: (
				<div className={styles.portSelector}>
					<CountryFlag
						countryCode={typeof p?.countryCode === 'string' ? p.countryCode : undefined}
					/>
					<p>{p.name}</p>
				</div>
			),
			value: p.id,
			searchValue: p.name,
		};
	}).sort((a, b) => a.searchValue.localeCompare(b.searchValue)) : []), [ports]);

	useEffect(() => {
		// Update the selected cargo-port with updated values from endpoint. Not very optimal.
		if (cargoDetails != null && selectedCargoPort != null) {
			const { id: cpId } = selectedCargoPort;
			const updated = cargoPorts.find((cp) => cp.id === cpId);

			if (updated == null) {
				return;
			}

			if (!isEqual(updated, selectedCargoPort)) {
				setSelectedCargoPort(updated);
				setActiveTabKey(String(updated.id));
			}
		}
	}, [cargoDetails, cargoPorts, selectedCargoPort]);

	const tabs = useMemo(() => sortedCargoPorts.map((cp) => ({
		key: cp.id,
		tabKey: `${cp.id}`,
		details: cp,
	})), [sortedCargoPorts]);

	const updateEntry = async ({ port, cargoPort }: {
		port: Port;
		cargoPort: CargoPortWithEstimatedValues;
	}) => {
		handleChange({
			cargoPortId: cargoPort.id,
			cargoId: cargoPort.cargoId,
			attributes: { port },
		});
	};

	const getEditBtnContent = useCallback((id: number) => {
		if (creatingCargo) {
			return null;
		}

		return (
			<div className={styles.innerBtnContainer}>
				<Button
					type="link"
					onClick={() => {
						setEditingPort(true);
						setPortToEdit(id);
					}}
					disabled={disabled}
					disabledTooltip="Cannot change port on fixed cargo"
					icon={(<EditOutlined />)}
				/>
			</div>
		);
	}, [creatingCargo, disabled]);

	const handlePortSelect = async (
		portId: number | null,
		cargoPort: CargoPortWithEstimatedValues,
	) => {
		if (portId == null || cargoPort == null) {
			return;
		}

		const newPort = ports?.find((p) => p.id === portId);

		if (newPort == null) {
			return;
		}

		await updateEntry({
			port: newPort,
			cargoPort,
		});

		setEditingPort(false);
	};

	return (
		<Card
			className={styles.card}
		>
			{(tabs == null || tabs.length === 0) && (
				<Empty 
					style={{ margin: 20 }} 
					description="No cargo ports added. Please add loading or discharge ports on the left hand side." 
				/>
			)}
			{tabs.length > 0 && (
				<Tabs
					onChange={(id) => {
						setActiveTabKey(id);
						setSelectedCargoPort(
							sortedCargoPorts.find((cp) => String(cp.id) === id) ?? null,
						);
					}}
					activeKey={activeTabKey ?? (selectedCargoPort ? String(selectedCargoPort.id) : undefined)}
					items={tabs.map((t) => {
						const { action } = splitActionKey(t.details.portAndActionKey);
						const fn = action === PortActionTypes.LOADING ? isLoadPort : isDischargePort;

						const filtered = sortedCargoPorts.filter(fn);

						const showCopyBtn = filtered.length > 1;

						return {
							key: t.key.toString(),
							label: (
								<Space>
									<Tag color={action === 'loading' ? 'geekblue' : 'red'}>
										{capitalize(action)}
									</Tag>
									<CountryFlag countryCode={t.details.port.countryCode} />
									{t.details.port.name}
								</Space>
							),
							children: (
								<Row gutter={[16, 16]}>
									{cargoPorts.length === 0 ? (
										<Col span={24}>
											<Empty description="Please add a cargo port to get started" />
										</Col>
									) : (
										<>
											<Col className={styles.cardPadding} span={24}>
												<Typography.Title level={4}>
													<Flex align="center" justify="space-between">
														<Flex gap={10} align="center">
															{`${capitalize(action)} in `}
															<div className={classNames(styles.flagAndNameRow)}>
																{editingPort && portToEdit === t.details.id ? (
																	<Select
																		showSearch
																		disabled={disabled}
																		className={styles.selectPort}
																		value={t.details.port.id}
																		key={t.details.port.id}
																		optionFilterProp="searchValue"
																		options={portOptions}
																		onSelect={async (
																			portId
																		) => await handlePortSelect(portId, t.details)}
																		onBlur={() => {
																			setEditingPort(false);
																			setPortToEdit(undefined);
																		}}
																	/>
																) : (
																	<>
																		<CountryFlag
																			countryCode={t.details.port.countryCode}
																			className={styles.portFlag}
																			size={25}
																		/>
																		<Typography.Text className={styles.portName}>
																			{t.details.port.name}
																		</Typography.Text>
																	</>
																)}
															</div>
															<div className={styles.topButtonRow}>
																{editingPort && portToEdit === t.details.id ? (
																	<Button
																		type="link"
																		className={styles.closeIcon}
																		onClick={() => {
																			setEditingPort(false);
																			setPortToEdit(undefined);
																		}}
																		icon={(<CloseOutlined />)}
																	/>
																) : (
																	getEditBtnContent(t.details.id)
																)}
															</div>
														</Flex>
														{showCopyBtn && (
															<Button
																type="primary"
																icon={(<FontAwesomeIcon color="white" icon={faCopy} />)}
																onClick={() => copyCargoPortSettings(t.details.id)}
															>
																Apply to all
																{' '}
																{action === 'loading' ? 'load' : 'discharge'}
																{' '}
																ports
															</Button>
														)}

													</Flex>
												</Typography.Title>
												<Row gutter={[24, 24]}>
													<Col span={24}>
														<Typography.Title level={5}>Charter party</Typography.Title>
														{cargoDetails != null && (
															<CargoPortForm
																mode={mode}
																key={t.details.id}
																handleCargoPortChange={(values) => {
																	handleChange({
																		...values,
																		attributes: {
																			port: t.details.port,
																			...values.attributes,
																		}
																	});
																}}
																refreshCargoDetails={refreshDetails}
																cargoPort={t.details}
																cargo={cargoDetails}
																enableDemDes={laytimeTerms === LaytimeTerms.NON_REVERSIBLE}
																type={
																	splitActionKey(t.details.portAndActionKey).action ===
																		'loading' ?
																		'loadingPort' :
																		'dischargePort'
																}
																quantitiesRemaining={quantitiesRemaining}
															/>
														)}
													</Col>
													{mode === 'estimate' && (
														<Col span={24}>
															<Typography.Title level={5}>
																Estimated calculation
															</Typography.Title>
															{cargoDetails != null && t.details != null && (
																<EstimatedCargoPortForm
																	key={t.details.id}
																	handleCargoPortChange={handleChange}
																	cargoPort={t.details}
																	cargo={cargoDetails}
																	refreshCargoDetails={refreshDetails}
																/>
															)}
														</Col>
													)}
												</Row>
											</Col>
										</>
									)}
								</Row>
							),
						};
					})}
				/>

			)}
		</Card>
	);
};
